import { toast } from 'sonner';
import i18next from 'i18next';

import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { useIntegrationConnectionStore } from '@/store/integration-connection.store.js';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';

const NS = 'thirdPartyApplications';

export class IntegrationConnectionIntegration {
    constructor(service) {
        this.service = service;
    }

    _err(error) {
        const data = error?.response?.data;
        if (data?.details?.fieldErrors && typeof data.details.fieldErrors === 'object') {
            const parts = Object.entries(data.details.fieldErrors)
                .flatMap(([field, msgs]) => {
                    const list = Array.isArray(msgs) ? msgs : [msgs];
                    return list.filter(Boolean).map((m) => `${field}: ${m}`);
                });
            if (parts.length) {
                const head = getIntegrationErrorMessage({ message: data.error })
                    || i18next.t('common:messages.validationError', {
                        defaultValue: 'Please check the information you entered',
                    });
                return `${head} — ${parts.join('; ')}`;
            }
        }
        if (data?.detail) {
            const head = getIntegrationErrorMessage({ message: data.error })
                || i18next.t('common:messages.error', { defaultValue: 'An error occurred' });
            return `${head}: ${data.detail}`;
        }
        return getIntegrationErrorMessage(error)
            || i18next.t('common:messages.unknownError', { defaultValue: 'An error occurred' });
    }

    // LEGACY-BYPASS: LB-20260914-04 — temporary console diagnostics for mapping create/publish failures
    /** Temporary diagnostics — remove after mapping create/publish root cause is fixed. */
    _diagMapping(action, info) {
         
        console.groupCollapsed(`[IntegrationMapping:DIAG] ${action}`);
         
        console.log(info);
         
        console.groupEnd();
    }

    _diagMappingError(action, connectionId, mappingId, payload, error) {
        const data = error?.response?.data;
        this._diagMapping(`${action} FAILED`, {
            connectionId,
            mappingId: mappingId || null,
            httpStatus: error?.response?.status,
            responseBody: data,
            responseError: data?.error,
            responseDetail: data?.detail,
            responseCode: data?.code,
            fieldErrors: data?.details?.fieldErrors,
            requestPayload: payload,
            axiosMessage: error?.message,
        });
    }

    async fetchAll(params = {}) {
        const store = useIntegrationConnectionStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const { columnFilters, ...requestParams } = params;
            const response = await this.service.getAll({
                ...requestParams,
                ...mapColumnFiltersForQuery(columnFilters),
            });
            const list = Array.isArray(response?.data) ? response.data : [];
            const meta = response?.pagination || {};
            store.setItems(list.map((it) => ({ ...it, id: it._id || it.id })));
            store.setPaginationData({
                currentPage: meta.page ?? params.page ?? 1,
                totalPages: meta.totalPages ?? 1,
                totalItems: meta.total ?? list.length,
                itemsPerPage: meta.pageSize ?? params.pageSize ?? 20,
                hasNextPage: meta.hasNextPage,
                hasPreviousPage: meta.hasPreviousPage,
            });
            store.setUniqueValues(response?.uniqueValues || {});
            return { success: true, data: list };
        } catch (error) {
            const msg = this._err(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchProviders() {
        const store = useIntegrationConnectionStore.getState();
        try {
            const response = await this.service.listProviders();
            store.setProviders(response.data || []);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async fetchById(id) {
        const store = useIntegrationConnectionStore.getState();
        store.setDetailLoading(true);
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setDetailLoading(false);
        }
    }

    async create(payload) {
        const store = useIntegrationConnectionStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(payload);
            toast.success(
                i18next.t(`${NS}:messages.createSuccess`, { defaultValue: 'Connection created' }),
            );
            store.closeForm();
            await this.fetchAll({ ...store.searchParams, columnFilters: store.columnFilters });
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async update(id, payload) {
        const store = useIntegrationConnectionStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, payload);
            toast.success(
                i18next.t(`${NS}:messages.updateSuccess`, { defaultValue: 'Connection updated' }),
            );
            store.closeForm();
            await this.fetchAll({ ...store.searchParams, columnFilters: store.columnFilters });
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async delete(id) {
        const store = useIntegrationConnectionStore.getState();
        store.setLoading(true);
        try {
            await this.service.delete(id);
            toast.success(
                i18next.t(`${NS}:messages.deleteSuccess`, { defaultValue: 'Connection deleted' }),
            );
            store.closeDeleteDialog();
            await this.fetchAll({ ...store.searchParams, columnFilters: store.columnFilters });
            return { success: true };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async test(id) {
        try {
            const response = await this.service.test(id);
            if (response.success) {
                toast.success(
                    i18next.t(`${NS}:messages.testSuccess`, { defaultValue: 'Connection test succeeded' }),
                );
            } else {
                toast.error(
                    getIntegrationErrorMessage({ message: response.error })
                    || i18next.t(`${NS}:messages.testFailed`, { defaultValue: 'Connection test failed' }),
                );
            }
            const store = useIntegrationConnectionStore.getState();
            await this.fetchAll({ ...store.searchParams, columnFilters: store.columnFilters });
            return response;
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async fetchEndpoints(connectionId) {
        const store = useIntegrationConnectionStore.getState();
        store.setEndpointsLoading(true);
        try {
            const response = await this.service.listEndpoints(connectionId);
            store.setEndpoints(response.data || []);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setEndpointsLoading(false);
        }
    }

    async createEndpoint(connectionId, payload) {
        try {
            const response = await this.service.createEndpoint(connectionId, payload);
            toast.success(
                i18next.t(`${NS}:messages.endpointCreateSuccess`, { defaultValue: 'Endpoint created' }),
            );
            await this.fetchEndpoints(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async updateEndpoint(connectionId, endpointId, payload) {
        try {
            const response = await this.service.updateEndpoint(connectionId, endpointId, payload);
            toast.success(
                i18next.t(`${NS}:messages.endpointUpdateSuccess`, { defaultValue: 'Endpoint updated' }),
            );
            await this.fetchEndpoints(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async deleteEndpoint(connectionId, endpointId) {
        try {
            await this.service.deleteEndpoint(connectionId, endpointId);
            toast.success(
                i18next.t(`${NS}:messages.endpointDeleteSuccess`, { defaultValue: 'Endpoint deleted' }),
            );
            await this.fetchEndpoints(connectionId);
            return { success: true };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async invoke(connectionId, endpointKey, body = {}, query = {}) {
        const store = useIntegrationConnectionStore.getState();
        store.setExplorerLoading(true);
        try {
            const response = await this.service.invokeEndpoint(connectionId, endpointKey, body, query);
            store.setExplorerResult(response.data || null);
            if (response.data?.mode === 'multi') {
                const views = response.data.views || [];
                const current = store.explorerActiveView;
                const stillValid = views.some((v) => v.key === current);
                if (!stillValid) {
                    store.setExplorerActiveView(views[0]?.key || null);
                }
            }
            if (!response.success) {
                toast.error(
                    getIntegrationErrorMessage({ message: response.error })
                    || i18next.t(`${NS}:messages.invokeFailed`, { defaultValue: 'Invoke failed' }),
                );
            }
            return response;
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            store.setExplorerResult(null);
            return { success: false, error: msg };
        } finally {
            store.setExplorerLoading(false);
        }
    }

    async fetchMarketplace(params = {}) {
        const store = useIntegrationConnectionStore.getState();
        store.setMarketplaceLoading(true);
        try {
            const response = await this.service.listMarketplace(params);
            store.setMarketplace(response.data || []);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setMarketplaceLoading(false);
        }
    }

    async fetchProviderDefinitions(params = {}) {
        const store = useIntegrationConnectionStore.getState();
        store.setProviderDefinitionsLoading?.(true);
        try {
            const response = await this.service.listProviderDefinitions(params);
            store.setProviderDefinitions?.(response.data || []);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setProviderDefinitionsLoading?.(false);
        }
    }

    async createProviderDefinition(data) {
        try {
            const response = await this.service.createProviderDefinition(data);
            toast.success(
                i18next.t(`${NS}:messages.providerCreateSuccess`, {
                    defaultValue: 'Custom provider saved',
                }),
            );
            await this.fetchProviderDefinitions();
            await this.fetchMarketplace();
            await this.fetchProviders();
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async updateProviderDefinition(id, data) {
        try {
            const response = await this.service.updateProviderDefinition(id, data);
            toast.success(
                i18next.t(`${NS}:messages.providerUpdateSuccess`, {
                    defaultValue: 'Custom provider updated',
                }),
            );
            await this.fetchProviderDefinitions();
            await this.fetchMarketplace();
            await this.fetchProviders();
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async publishProviderDefinition(id, data = {}) {
        return this.updateProviderDefinition(id, {
            ...data,
            status: 'published',
            isCurrent: true,
            isActive: true,
        });
    }

    async fetchResources(connectionId) {
        const store = useIntegrationConnectionStore.getState();
        store.setResourcesLoading(true);
        try {
            const response = await this.service.listResources(connectionId);
            store.setResources(response.data || []);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setResourcesLoading(false);
        }
    }

    async discoverResource(connectionId, resourceKey, body = {}) {
        const store = useIntegrationConnectionStore.getState();
        store.setDiscoverLoading(true);
        try {
            const response = await this.service.discoverResource(connectionId, resourceKey, body);
            const payload = response.data || {};
            store.setDiscoveredSchema(payload.schema || null);
            store.setDiscoveredFields(payload.fields || []);
            store.setDiscoveredFingerprint(payload.fingerprint || null);
            store.setDiscoverWarnings(payload.warnings || []);
            store.setDiscoveredSamples(payload.samples || payload.rows || []);
            if ((payload.warnings || []).includes('NO_ROWS') || !(payload.samples || []).length) {
                // Keep silent on empty — wizard shows inline warning; toast only for hard errors
            }
            return { success: true, data: payload };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            store.setDiscoveredSchema(null);
            store.setDiscoveredFields([]);
            store.setDiscoveredFingerprint(null);
            store.setDiscoverWarnings([error?.code || 'DISCOVER_FAILED']);
            store.setDiscoveredSamples([]);
            return { success: false, error: msg, code: error?.code };
        } finally {
            store.setDiscoverLoading(false);
        }
    }

    async fetchTargetFields(connectionId, entityType) {
        const store = useIntegrationConnectionStore.getState();
        store.setTargetFieldsLoading(true);
        try {
            const response = await this.service.listTargetFields(connectionId, entityType);
            // API returns { entityType, writableFields, partyRoles, customFields } — not a field array.
            const payload = response.data;
            const rawFields = Array.isArray(payload)
                ? payload
                : [
                    ...(payload?.writableFields || []),
                    ...(payload?.customFields || []),
                ];
            const fields = rawFields.map((f) => {
                if (typeof f === 'string') {
                    return { path: f, key: f, required: f === 'assetName' || f === 'title' };
                }
                const path = f.path || f.key || f.name;
                return { ...f, path, key: f.key || path };
            }).filter((f) => f.path);
            store.setTargetFields(fields);
            return { success: true, data: fields, meta: payload };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            store.setTargetFields([]);
            return { success: false, error: msg };
        } finally {
            store.setTargetFieldsLoading(false);
        }
    }

    async fetchMappings(connectionId, params = {}) {
        const store = useIntegrationConnectionStore.getState();
        store.setMappingsLoading(true);
        try {
            const response = await this.service.listMappings(connectionId, params);
            store.setMappings(response.data || []);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setMappingsLoading(false);
        }
    }

    async fetchMapping(connectionId, mappingId) {
        const store = useIntegrationConnectionStore.getState();
        store.setMappingDetailLoading(true);
        try {
            const response = await this.service.getMapping(connectionId, mappingId);
            store.setSelectedMapping(response.data || null);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setMappingDetailLoading(false);
        }
    }

    async createMapping(connectionId, payload, opts = {}) {
        const silent = Boolean(opts.silent);
        this._diagMapping('createMapping REQUEST', { connectionId, payload });
        try {
            const response = await this.service.createMapping(connectionId, payload);
            this._diagMapping('createMapping SUCCESS', { connectionId, data: response.data });
            if (!silent) {
                toast.success(
                    i18next.t(`${NS}:messages.mappingCreateSuccess`, { defaultValue: 'Mapping created' }),
                );
            }
            if (!opts.skipFetch) await this.fetchMappings(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            this._diagMappingError('createMapping', connectionId, null, payload, error);
            const msg = this._err(error);
            if (!silent) toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async updateMapping(connectionId, mappingId, payload) {
        this._diagMapping('updateMapping REQUEST', { connectionId, mappingId, payload });
        try {
            const response = await this.service.updateMapping(connectionId, mappingId, payload);
            this._diagMapping('updateMapping SUCCESS', { connectionId, mappingId, data: response.data });
            toast.success(
                i18next.t(`${NS}:messages.mappingUpdateSuccess`, { defaultValue: 'Mapping updated' }),
            );
            await this.fetchMappings(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            this._diagMappingError('updateMapping', connectionId, mappingId, payload, error);
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async deleteMapping(connectionId, mappingId) {
        try {
            const response = await this.service.deleteMapping(connectionId, mappingId);
            toast.success(
                i18next.t(`${NS}:messages.mappingDeleteSuccess`, { defaultValue: 'Mapping deleted' }),
            );
            await this.fetchMappings(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async cloneMapping(connectionId, mappingId, payload = {}) {
        this._diagMapping('cloneMapping REQUEST', { connectionId, mappingId, payload });
        try {
            const response = await this.service.cloneMapping(connectionId, mappingId, payload);
            this._diagMapping('cloneMapping SUCCESS', { connectionId, mappingId, data: response.data });
            toast.success(
                i18next.t(`${NS}:messages.mappingCloneSuccess`, { defaultValue: 'Mapping cloned' }),
            );
            await this.fetchMappings(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            this._diagMappingError('cloneMapping', connectionId, mappingId, payload, error);
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async publishMapping(connectionId, mappingId, payload = {}, opts = {}) {
        const silent = Boolean(opts.silent);
        this._diagMapping('publishMapping REQUEST', { connectionId, mappingId, payload });
        try {
            const response = await this.service.publishMapping(connectionId, mappingId, payload);
            this._diagMapping('publishMapping SUCCESS', { connectionId, mappingId, data: response.data });
            if (!silent) {
                toast.success(
                    i18next.t(`${NS}:messages.mappingPublishSuccess`, { defaultValue: 'Mapping published' }),
                );
            }
            if (!opts.skipFetch) await this.fetchMappings(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            this._diagMappingError('publishMapping', connectionId, mappingId, payload, error);
            const msg = this._err(error);
            if (!silent) toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async previewMapping(connectionId, mappingId, payload = {}, opts = {}) {
        const silent = Boolean(opts.silent);
        const store = useIntegrationConnectionStore.getState();
        store.setPreviewLoading(true);
        this._diagMapping('previewMapping REQUEST', { connectionId, mappingId, payload, silent });
        try {
            const response = await this.service.previewMapping(connectionId, mappingId, payload);
            const data = response.data || {};
            const rows = (data.rows || data.previewRows || []).map((row, index) => ({
                ...row,
                ...(row.candidate && typeof row.candidate === 'object' ? row.candidate : {}),
                id: row.id ?? index,
            }));
            store.setPreviewRows(rows);
            store.setPreviewMeta({
                previewToken: data.previewToken,
                counts: data.counters || data.counts,
                warnings: data.warnings || [],
                runId: data.runId,
                rowSourceMeta: data.rowSourceMeta || null,
                ...(data.meta || {}),
            });
            if (!silent && (data.warnings || []).includes('EXTERNAL_ID_NULL')) {
                toast.error(
                    i18next.t(`${NS}:wizard.preview.externalIdNull`, {
                        defaultValue: 'Preview rows have null externalId. Set External ID source path to a field that exists on source rows (e.g. host_id for Nessus hosts).',
                    }),
                );
            }
            this._diagMapping('previewMapping SUCCESS', {
                connectionId,
                mappingId,
                rowCount: rows.length,
                sampleExternalIds: rows.slice(0, 3).map((r) => r.externalId),
                warnings: data.warnings,
            });
            return { success: true, data };
        } catch (error) {
            this._diagMappingError('previewMapping', connectionId, mappingId, payload, error);
            const msg = this._err(error);
            if (!silent) toast.error(msg);
            store.setPreviewRows([]);
            store.setPreviewMeta(null);
            return { success: false, error: msg };
        } finally {
            store.setPreviewLoading(false);
        }
    }

    clearPreviewState() {
        const store = useIntegrationConnectionStore.getState();
        store.setPreviewRows([]);
        store.setPreviewMeta(null);
        store.setPreviewLoading(false);
    }

    async importMapping(connectionId, mappingId, payload, opts = {}) {
        const silent = Boolean(opts.silent);
        try {
            const response = await this.service.importMapping(connectionId, mappingId, payload);
            if (!silent) {
                toast.success(
                    i18next.t(`${NS}:messages.importSuccess`, { defaultValue: 'Import started' }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            if (!silent) toast.error(msg);
            return { success: false, error: msg };
        }
    }

    /**
     * Orchestrate provider importRecipes: create → publish → preview → import per step.
     * Single summary toast at the end (silent per-step mutations).
     */
    async runImportRecipe({
        connectionId,
        recipe,
        templates,
        pathParams = {},
        onProgress = null,
    } = {}) {
        if (!connectionId || !recipe?.steps?.length) {
            return { success: false, error: 'Recipe missing' };
        }

        const silent = { silent: true, skipFetch: true };
        const stepResults = [];
        let failed = false;
        let partial = false;

        try {
            for (let i = 0; i < recipe.steps.length; i += 1) {
                const step = recipe.steps[i];
                const tpl = typeof templates?.get === 'function'
                    ? templates.get(step.templateKey)
                    : templates?.[step.templateKey];
                if (!tpl) {
                    failed = true;
                    stepResults.push({ templateKey: step.templateKey, error: 'Template not found' });
                    break;
                }

                if (typeof onProgress === 'function') {
                    onProgress(i === 0 ? 'host' : 'vuln', step);
                }

                const suffix = Date.now().toString(36).slice(-4);
                const createPayload = {
                    connectionId,
                    key: `${tpl.key}-${suffix}`,
                    name: tpl.name,
                    description: tpl.description,
                    resourceKey: tpl.resourceKey,
                    targetEntityType: tpl.targetEntityType,
                    externalIdSourcePath: tpl.externalIdSourcePath,
                    ...(tpl.externalIdTransformExpression
                        ? { externalIdTransformExpression: tpl.externalIdTransformExpression }
                        : {}),
                    resourceContext: {
                        ...(tpl.resourceContext || {}),
                        pathParams: {
                            ...((tpl.resourceContext && tpl.resourceContext.pathParams) || {}),
                            ...pathParams,
                        },
                    },
                    ...(tpl.rowSource ? { rowSource: tpl.rowSource } : {}),
                    fieldMappings: tpl.fieldMappings || [],
                    referenceMappings: tpl.referenceMappings || [],
                };

                const created = await this.createMapping(connectionId, createPayload, silent);
                if (!created.success) {
                    failed = true;
                    stepResults.push({ templateKey: step.templateKey, error: created.error });
                    break;
                }
                const mappingId = created.data?._id || created.data?.id;

                if (step.publish !== false) {
                    const published = await this.publishMapping(connectionId, mappingId, {}, silent);
                    if (!published.success) {
                        failed = true;
                        stepResults.push({ templateKey: step.templateKey, error: published.error });
                        break;
                    }
                }

                if (step.import !== false) {
                    const preview = await this.previewMapping(
                        connectionId,
                        mappingId,
                        {
                            sampleLimit: 100,
                            pathParams,
                            multiViewKey: createPayload.resourceContext?.multiViewKey,
                        },
                        { silent: true },
                    );
                    if (!preview.success || !preview.data?.previewToken) {
                        failed = true;
                        stepResults.push({
                            templateKey: step.templateKey,
                            error: preview.error || 'Preview failed',
                        });
                        break;
                    }
                    const imported = await this.importMapping(
                        connectionId,
                        mappingId,
                        { previewToken: preview.data.previewToken },
                        { silent: true },
                    );
                    if (!imported.success) {
                        partial = true;
                        stepResults.push({ templateKey: step.templateKey, error: imported.error });
                    } else {
                        stepResults.push({
                            templateKey: step.templateKey,
                            mappingId,
                            runId: imported.data?.runId,
                            counters: imported.data?.counters || preview.data?.counters,
                        });
                    }
                } else {
                    stepResults.push({ templateKey: step.templateKey, mappingId });
                }
            }

            await this.fetchMappings(connectionId);

            if (failed) {
                toast.error(
                    i18next.t(`${NS}:recipe.failed`, { defaultValue: 'Scan import failed' }),
                );
                return { success: false, partial, stepResults, error: stepResults.at(-1)?.error };
            }

            if (partial) {
                toast.error(
                    i18next.t(`${NS}:recipe.partial`, {
                        defaultValue: 'Scan import finished with some errors — check Runs',
                    }),
                );
                return { success: true, partial: true, stepResults };
            }

            toast.success(
                i18next.t(`${NS}:recipe.success`, { defaultValue: 'Scan import finished' }),
            );
            return { success: true, partial: false, stepResults };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg, stepResults };
        }
    }

    async fetchRuns(connectionId, params = {}) {
        const store = useIntegrationConnectionStore.getState();
        store.setRunsLoading(true);
        try {
            const response = await this.service.listRuns(connectionId, params);
            store.setRuns(response.data || []);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setRunsLoading(false);
        }
    }

    async fetchRun(connectionId, runId) {
        const store = useIntegrationConnectionStore.getState();
        store.setRunDetailLoading(true);
        try {
            const response = await this.service.getRun(connectionId, runId);
            store.setSelectedRun(response.data || null);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setRunDetailLoading(false);
        }
    }

    async fetchRunItems(connectionId, runId, params = {}) {
        const store = useIntegrationConnectionStore.getState();
        store.setRunItemsLoading(true);
        try {
            const response = await this.service.listRunItems(connectionId, runId, params);
            store.setRunItems(response.data || []);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setRunItemsLoading(false);
        }
    }

    async replayRunFailures(connectionId, runId, payload = {}) {
        try {
            const response = await this.service.replayRunFailures(connectionId, runId, payload);
            toast.success(
                i18next.t(`${NS}:messages.replaySuccess`, { defaultValue: 'Failure replay queued' }),
            );
            await this.fetchRuns(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async fetchSchedules(connectionId) {
        const store = useIntegrationConnectionStore.getState();
        store.setSchedulesLoading(true);
        try {
            const response = await this.service.listSchedules(connectionId);
            store.setSchedules(response.data || []);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setSchedulesLoading(false);
        }
    }

    async upsertSchedule(connectionId, payload) {
        try {
            const response = await this.service.upsertSchedule(connectionId, payload);
            toast.success(
                i18next.t(`${NS}:messages.scheduleSaveSuccess`, { defaultValue: 'Schedule saved' }),
            );
            await this.fetchSchedules(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async runScheduleNow(connectionId, syncStateId) {
        try {
            const response = await this.service.runScheduleNow(connectionId, syncStateId);
            toast.success(
                i18next.t(`${NS}:messages.scheduleRunSuccess`, { defaultValue: 'Sync run queued' }),
            );
            await this.fetchSchedules(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async pauseSchedule(connectionId, syncStateId, payload = {}) {
        try {
            const response = await this.service.pauseSchedule(connectionId, syncStateId, payload);
            toast.success(
                i18next.t(`${NS}:messages.schedulePauseSuccess`, { defaultValue: 'Schedule updated' }),
            );
            await this.fetchSchedules(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async resetCheckpoint(connectionId, syncStateId, payload = {}) {
        try {
            const response = await this.service.resetCheckpoint(connectionId, syncStateId, payload);
            toast.success(
                i18next.t(`${NS}:messages.checkpointResetSuccess`, { defaultValue: 'Checkpoint reset' }),
            );
            await this.fetchSchedules(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async executeAction(connectionId, payload) {
        try {
            const response = await this.service.executeAction(connectionId, payload);
            if (response.success) {
                toast.success(
                    i18next.t(`${NS}:messages.actionExecuteSuccess`, { defaultValue: 'Action completed' }),
                );
            } else {
                toast.error(
                    getIntegrationErrorMessage({ message: response.error })
                    || i18next.t(`${NS}:messages.actionExecuteFailed`, { defaultValue: 'Action failed' }),
                );
            }
            return response;
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async resolveConflict(connectionId, itemId, payload) {
        try {
            const response = await this.service.resolveConflict(connectionId, itemId, payload);
            toast.success(
                i18next.t(`${NS}:messages.conflictResolveSuccess`, { defaultValue: 'Conflict resolved' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async upgradeProvider(connectionId, payload) {
        try {
            const response = await this.service.upgradeProvider(connectionId, payload);
            toast.success(
                i18next.t(`${NS}:messages.upgradeSuccess`, { defaultValue: 'Provider upgrade completed' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getWebhookStatus(connectionId) {
        try {
            const response = await this.service.getWebhookStatus(connectionId);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async enableWebhook(connectionId) {
        try {
            const response = await this.service.enableWebhook(connectionId);
            toast.success(
                i18next.t(`${NS}:messages.webhookEnableSuccess`, {
                    defaultValue: 'Inbound webhook enabled',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async rotateWebhookSecret(connectionId) {
        try {
            const response = await this.service.rotateWebhookSecret(connectionId);
            toast.success(
                i18next.t(`${NS}:messages.webhookRotateSuccess`, {
                    defaultValue: 'Webhook secret rotated',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async disableWebhook(connectionId) {
        try {
            const response = await this.service.disableWebhook(connectionId);
            toast.success(
                i18next.t(`${NS}:messages.webhookDisableSuccess`, {
                    defaultValue: 'Inbound webhook disabled',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async loadWorkspaceConnection(connectionId) {
        const store = useIntegrationConnectionStore.getState();
        store.setDetailLoading(true);
        try {
            const response = await this.service.getById(connectionId);
            const { sanitizeConnectionForStore } = await import(
                '@/lib/integration-connection.helpers.js'
            );
            const data = sanitizeConnectionForStore(response.data || null);
            store.setWorkspaceConnection(data);
            return { success: true, data };
        } catch (error) {
            const msg = this._err(error);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setDetailLoading(false);
        }
    }
}
